import { UNKNOWN_PERSON_NAME } from "@remi/constants";
import { FORBIDDEN_NAME_REGEX } from "@remi/types/person";

/**
 * @description Split full name into first and last name parts
 * @example "John Bob Doe" => ["John Bob", "Doe"]
 * @param fullName Full name to split
 * @returns [firstName, lastName]
 */
export function splitFullName(fullName: string | null | undefined): [string, string] {
  if (!fullName) {
    return ["", ""];
  }
  const names = titleCase(fullName).split(new RegExp("\\s+"));
  if (names.length < 2) {
    return [names[0] ?? "", ""];
  }
  return [names.slice(0, -1).join(" "), names[names.length - 1] ?? ""];
}

/**
 * Converts a string into title case
 * @example jim-bo bo_joe_row => Jim-Bo Bo Joe Row
 * @param s String to Convert
 * @returns Title case string
 */
export function titleCase(s: string | null | undefined): string {
  if (!s) {
    return "";
  }
  const trimmed = s.toLowerCase().trim();
  const underscoresRemoved = trimmed.replace(/_/g, " ");
  const titleCaseString = underscoresRemoved.replace(
    /\w*/g,
    (match) => match.charAt(0).toUpperCase() + match.substring(1).toLowerCase(),
  );
  return titleCaseString;
}

/**
 * Converts a string into sentence case
 * @example jim-bo bo_joe_row => Jim-bo Bo joe row
 * @param s String to Convert
 * @returns Sentence case string
 */
export function sentenceCase(s: string | null | undefined): string {
  if (!s) {
    return "";
  }
  const trimmed = s.toLowerCase().trim();
  const underscoresRemoved = trimmed.replace(/_/g, " ");
  const sentenceCaseString = underscoresRemoved.replace(/^./, (match) => match.toUpperCase());
  return sentenceCaseString;
}

/**
 * Format a full name for display.
 * @example "John Doe"
 * @param firstName First name to format.
 * @param lastName Last name to format.
 * @returns The formatted full name.
 */
export function formatFullName(firstName: string | null | undefined, lastName: string | null | undefined): string {
  if (!firstName && !lastName) {
    return "";
  }
  if (!firstName) {
    return titleCase(lastName);
  }
  if (!lastName) {
    return titleCase(firstName);
  }
  return `${titleCase(firstName)} ${formatLastName(lastName)}`;
}

export function formatPersonFullName(person: { firstName: string; lastName: string }): string {
  return formatFullName(person.firstName, person.lastName);
}

export function sanitizePersonName(name: string): string {
  return name.replace(FORBIDDEN_NAME_REGEX, "").trim();
}

/**
 * Build a Contact.name from a Person's first/last. Sanitizes both parts and
 * trims the joiner — the canonical `Contact.name` representation for type=PERSON.
 */
export function formatPersonContactName(firstName: string, lastName: string): string {
  return `${sanitizePersonName(firstName)} ${sanitizePersonName(lastName)}`.trim();
}

/**
 * Split a Contact.name into [firstName, lastName] without titleCasing.
 *
 * Inverse of `formatPersonContactName` for the common case. Preserves multi-word
 * last names ("Mary Van Houten" → ["Mary", "Van Houten"]) and mixed-case names
 * unchanged ("McDonald" stays "McDonald"). Single-word names yield empty
 * lastName.
 *
 * Use this for in-app form seeding and for round-tripping Contact.name through
 * external APIs that require first/last halves — never `splitFullName`, which
 * titleCases and corrupts mixed-case names.
 *
 * Whitespace is trimmed at the boundaries and at the join between firstName
 * and lastName, but internal whitespace within the lastName portion is NOT
 * collapsed ("Mary  Van  Houten" → ["Mary", "Van  Houten"]). This is safe
 * because Contact.name is always written via formatPersonContactName, which
 * joins parts with a single space.
 */
export function splitContactName(name: string): [string, string] {
  const trimmed = name.trim();
  const idx = trimmed.indexOf(" ");
  if (idx === -1) {
    return [trimmed, ""];
  }
  return [
    trimmed.slice(0, idx),
    trimmed
      .slice(idx + 1)
      .trim()
      .replace(/\s+/g, " "),
  ];
}

/**
 * Build a Contact.name for a row whose owner isn't yet known — typically a Phone
 * or EmailAddress without a Person link. Matches the existing inbound-call
 * pattern in `phoneDao.getOrCreatePhone` (`UNKNOWN_PERSON_NAME` = "Unknown").
 */
export function formatUnknownContactName(identifier: string): string {
  return `${identifier} ${UNKNOWN_PERSON_NAME}`;
}

// Shared so every internal/external name-coincidence check normalizes identically.
export function normalizePersonName(firstName: string, lastName: string): string {
  return `${firstName} ${lastName}`.trim().toLowerCase().replace(/\s+/g, " ");
}

function formatLastName(lastName: string): string {
  if (lastName.startsWith("Mc") && lastName.length > 2) {
    return `Mc${titleCase(lastName.slice(2))}`;
  }
  return titleCase(lastName);
}
